import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { Icon } from './Icon';

interface FilesSummary {
  files: number;
  bytes: number;
  archiveBytes: number;
  recorded: number;
  missing: number;
}

/** 1.4 GB, 380 MB, 12 KB — the way a download size is read. */
export function formatBytes(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} bytes`;
}

/**
 * Every uploaded file in one download, for moving the platform to a new
 * server. The database carries the passages, answers and results; this carries
 * the recordings, pictures, imported papers and logos that live on disk.
 *
 * The same panel is the check on the other side: on the new server, once the
 * files are unpacked, it says whether every file the database refers to is
 * there.
 */
export function ServerFiles() {
  const [summary, setSummary] = useState<FilesSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    api
      .get<FilesSummary>('/admin/files')
      .then(({ data }) => setSummary(data))
      .catch((err) => setError(errorMessage(err)));
  }, []);

  async function download() {
    setStarting(true);
    setError(null);
    try {
      const { data } = await api.post<{ url: string }>('/admin/files/archive-link');
      // A link the browser follows, so the file streams to disk with the
      // browser's own progress bar instead of being held in this page's memory.
      window.location.assign(data.url);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setStarting(false);
    }
  }

  return (
    <section className="panel stack serverfiles" aria-labelledby="serverfiles-title">
      <div>
        <h2 className="panel__title" id="serverfiles-title">
          Uploaded files
        </h2>
        <p className="muted">
          Listening recordings, pictures, imported papers and centres’ logos. To move the platform to
          a new server, download them all here and unpack them into its upload folder.
        </p>
      </div>

      {error && <p className="serverfiles__error">{error}</p>}

      {summary && (
        <dl className="serverfiles__facts">
          <div>
            <dt>Files</dt>
            <dd>{summary.files.toLocaleString('en-GB')}</dd>
          </div>
          <div>
            <dt>Download size</dt>
            <dd>{formatBytes(summary.archiveBytes)}</dd>
          </div>
          <div>
            <dt>Check</dt>
            <dd className={summary.missing ? 'serverfiles__bad' : 'serverfiles__good'}>
              {summary.recorded === 0
                ? 'No files recorded yet'
                : summary.missing === 0
                  ? `All ${summary.recorded.toLocaleString('en-GB')} files the database lists are here`
                  : `${summary.missing.toLocaleString('en-GB')} of ${summary.recorded.toLocaleString('en-GB')} files the database lists are missing`}
            </dd>
          </div>
        </dl>
      )}

      <div>
        <button
          className="btn"
          onClick={() => void download()}
          disabled={starting || !summary || summary.files === 0}
        >
          <Icon name="download" size={16} />
          {starting ? 'Starting…' : 'Download all files'}
        </button>
      </div>
    </section>
  );
}
